/**
 * Studio V2 API Client
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';
import type { StudioDetailV2, StudioReviewsResponseV2, StudioReviewV2 } from '@/types/discovery';
import { logger } from '@/lib/utils/logger';

/** V2 review fields are camelCase in the JSON (see StudioReviewResponse dto). Pagination stays snake_case. */
interface RawStudioReview {
  id: string;
  reviewerName: string;
  rating: number;
  text: string;
  createdAt: string;
  title?: string;
  userId?: string;
  studioId?: string;
  studioName?: string;
}

interface RawStudioReviewsResponse {
  reviews: RawStudioReview[];
  pagination: {
    cursor?: string;
    hasMore: boolean;
  };
}

function mapStudioReview(raw: RawStudioReview): StudioReviewV2 {
  return {
    id: raw.id,
    reviewerName: raw.reviewerName,
    rating: raw.rating,
    text: raw.text,
    createdAt: raw.createdAt,
    title: raw.title,
    userId: raw.userId,
    studioId: raw.studioId,
    studioName: raw.studioName,
  };
}

/**
 * Get studio detail by ID
 */
export const getStudio = async (studioId: string): Promise<Result<StudioDetailV2, ApiError>> => {
  try {
    logger.info('getStudio', 'Fetching studio details', {
      studioId,
      idLength: studioId.length,
      idFormat: studioId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
        ? 'UUID'
        : 'Unknown',
      url: `/v2/studios/${studioId}`,
    });

    const response = await apiClient.get<StudioDetailV2>(`/v2/studios/${studioId}`);

    if (!response.ok) {
      logger.error('getStudio', 'Failed to fetch studio details', {
        studioId,
        error: response.error,
        status: 'status' in response.error ? response.error.status : undefined,
      });
      return err(response.error);
    }

    logger.info('getStudio', 'Successfully fetched studio details', {
      studioId,
      studioName: response.value.name,
    });

    return ok(response.value);
  } catch (error) {
    logger.error('getStudio', 'Network error fetching studio details', {
      studioId,
      error,
    });
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch studio details',
    });
  }
};

/**
 * Get paginated studio reviews (cursor pagination; the opaque cursor carries the
 * offset, matching the /v2/me/reviews contract)
 */
export const getStudioReviews = async (
  studioId: string,
  options?: { limit?: number; cursor?: string }
): Promise<Result<StudioReviewsResponseV2, ApiError>> => {
  try {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) {
      params.append('limit', String(options.limit));
    }
    if (options?.cursor) {
      params.append('cursor', options.cursor);
    }

    const queryString = params.toString();
    const endpoint = `/v2/studios/${studioId}/reviews${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<StudioReviewsResponseV2>(endpoint);

    if (!response.ok) {
      return err(response.error);
    }

    const rawPagination = (response.value as unknown as RawStudioReviewsResponse).pagination;
    const mapped: StudioReviewsResponseV2 = {
      reviews: (response.value as unknown as RawStudioReviewsResponse).reviews.map(mapStudioReview),
      pagination: {
        cursor: rawPagination.cursor ?? '',
        hasMore: rawPagination.hasMore,
      },
    };
    return ok(mapped);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch studio reviews',
    });
  }
};

/**
 * Validation schema for review submission. Built lazily via dynamic import:
 * this module sits in the eager chunk (route defs import it for SSR loaders),
 * and a module-scope zod import would pin all of zod (~80KB) into that chunk
 * for every page even though only the review mutations need it.
 */
const getReviewValidationSchema = async () => {
  const { z } = await import('zod');
  return z.object({
    rating: z.number().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5'),
    text: z
      .string()
      .min(50, 'Review text must be at least 50 characters')
      .max(1000, 'Review text must not exceed 1000 characters'),
  });
};

/**
 * Create a new review for a studio
 */
export const createStudioReview = async (
  studioId: string,
  review: { rating: number; text: string }
): Promise<Result<StudioReviewV2, ApiError>> => {
  try {
    // Validate input
    const reviewValidationSchema = await getReviewValidationSchema();
    const validationResult = reviewValidationSchema.safeParse(review);
    if (!validationResult.success) {
      const errorMessages = validationResult.error.issues.map((issue) => issue.message).join(', ');
      return err({
        type: 'validation',
        status: 422,
        message: errorMessages,
      } as const);
    }

    const response = await apiClient.post<StudioReviewV2>(`/v2/studios/${studioId}/reviews`, {
      rating: review.rating,
      text: review.text,
    });

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(mapStudioReview(response.value as unknown as RawStudioReview));
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to submit review',
    });
  }
};

/**
 * Update an existing studio review
 */
export const updateStudioReview = async (
  studioId: string,
  reviewId: string,
  updates: { rating?: number; text?: string }
): Promise<Result<StudioReviewV2, ApiError>> => {
  try {
    // Validate updates if provided
    if (updates.rating !== undefined || updates.text !== undefined) {
      const reviewValidationSchema = await getReviewValidationSchema();
      const validationResult = reviewValidationSchema.partial().safeParse(updates);
      if (!validationResult.success) {
        const errorMessages = validationResult.error.issues
          .map((issue) => issue.message)
          .join(', ');
        return err({
          type: 'validation',
          status: 422,
          message: errorMessages,
        } as const);
      }
    }

    const response = await apiClient.patch<StudioReviewV2>(
      `/v2/studios/${studioId}/reviews/${reviewId}`,
      {
        ...(updates.rating !== undefined && { rating: updates.rating }),
        ...(updates.text !== undefined && { text: updates.text }),
      }
    );

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(mapStudioReview(response.value as unknown as RawStudioReview));
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to update review',
    });
  }
};

/**
 * Delete a studio review (soft delete)
 */
export const deleteStudioReview = async (
  studioId: string,
  reviewId: string
): Promise<Result<void, ApiError>> => {
  try {
    const response = await apiClient.delete<void>(`/v2/studios/${studioId}/reviews/${reviewId}`);

    if (!response.ok) {
      // Return generic error - can't check status on ApiError type
      return err(response.error);
    }

    return ok(undefined);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to delete review',
    });
  }
};
